import React, { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import TabsContent from "@/components/adg4/tabs-content/src/tabs-content";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

export type TabsDataType = {
    id: string;
    title: string;
    caption: string;
    englishLabel: string;
    tabComponent: React.ReactElement<typeof TabsContent>;
}[];

export type TabsWithCardsProps = PropsWithChildren<{
    readonly tabsData: TabsDataType;
    readonly selectedIndexOverride?: number;
}>;

export interface EnterpriseTabsWithCardsProps {
    tabsData: TabsDataType;
    tabSwitchInterval: number | undefined;
}

const Tab = ({
    title,
    caption,
    isSelected,
    dataEventProps
}: {
    title: string;
    caption: string;
    isSelected?: boolean;
    dataEventProps: DataEventProps;
}) => {
    const eyebrowStyle = css({
        fontFamily: "Charlie Text",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "18px" /* 128.571% */,
        textTransform: "uppercase",
        margin: "0 0 16px 0"
    });

    const captionStyle = css({
        fontFamily: "Charlie Display",
        fontSize: "18px",
        fontStyle: "normal",
        fontWeight: "700",
        lineHeight: "24px",
        textAlign: "left",
        margin: "0",
        alignSelf: "stretch",
        width: "100%"
    });

    return (
        <button
            css={[tabStyles, isSelected && currentStyle]}
            {...dataEventProps}
        >
            <span css={eyebrowStyle}>{title}</span>
            <span css={captionStyle}>{caption}</span>
        </button>
    );
};

export const EnterpriseTabsWithCards = ({
    tabsData,
    tabSwitchInterval
}: EnterpriseTabsWithCardsProps) => {
    return (
        <section id="tabs" css={sectionStyles}>
            <Adg4Tabs tabSwitchInterval={tabSwitchInterval} loop={true}>
                <div css={topSectionStyles}>
                    {tabsData.map((tab, index) => (
                        <Adg4TabPanel
                            panelIndex={index}
                            id={tab.id}
                            key={index}
                        >
                            {tab.tabComponent}
                        </Adg4TabPanel>
                    ))}
                </div>
                <div css={bottomSectionStyles}>
                    <Adg4DraggableTabList
                        css={tabList}
                        rowGap="12px"
                        className="tab-list"
                    >
                        {tabsData.map((tab, index) => (
                            <Adg4DraggableTab
                                key={index}
                                tabIndex={index}
                                className="round-tab"
                            >
                                <Tab
                                    title={tab.title}
                                    caption={tab.caption}
                                    dataEventProps={{
                                        "data-product-key": "home",
                                        "data-event": "clicked",
                                        "data-event-component": "button",
                                        "data-event-container": "tabsMenu",
                                        "data-label": tab.title,
                                        "data-label-english": tab.englishLabel
                                    }}
                                />
                            </Adg4DraggableTab>
                        ))}
                    </Adg4DraggableTabList>
                </div>
            </Adg4Tabs>
        </section>
    );
};

const topSectionStyles = css({
    position: "relative",
    display: "block"
});

const bottomSectionStyles = css({
    position: "relative",
    display: "block",
    zIndex: "2",
    insetBlockStart: "-110px"
});

const sectionStyles = css({
    width: "100%"
});

const tabList = css({
    padding: `0px 60px 37px`,
    display: "flex",
    alignItems: "stretch",
    gap: "32px",
    [media.below.sm]: {
        padding: `0px 32px 30px`
    }
});

const tabStyles = css({
    "display": "flex",
    "width": "fit-content",
    "height": "100%",
    "minHeight": "154px",
    "maxWidth": "176px",
    "padding": "24px",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "cursor": "pointer",
    "flexGrow": 1,
    "borderRadius": "20px",
    "border": "none",
    "background": "var(--Primary-White, #FFF)",
    "boxShadow":
        "0px 0px 0.25px 0px rgba(1, 31, 69, 0.31), 0px 2px 2.5px 0px rgba(1, 31, 69, 0.20)",
    "fontFamily": "var(--bxp-charlie-text-font-family)",
    "fontWeight": "400",
    "fontSize": "20px",
    "color": "#8A8F99",
    "insetBlockStart": "-50px",
    "transition": "ease-in-out 0.2s all",
    [media.only.xs]: {
        minHeight: "154px",
        maxWidth: "176px"
    },
    [media.above.xs]: {
        padding: "32px"
    },
    [media.only.sm]: {
        minHeight: "169px",
        maxWidth: "195px"
    },
    [media.only.md]: {
        minHeight: "146px",
        maxWidth: "202px"
    },
    [media.above.lg]: {
        minHeight: "146px",
        maxWidth: "268px"
    },
    "&:hover": {
        boxShadow:
            "0px 10px 20px 0px rgba(1, 31, 69, 0.20), 0px 0px 1px 0px rgba(1, 31, 69, 0.31)"
    }
});

const currentStyle = css({
    border: "2px solid #1868DB",
    boxShadow:
        "0px 10px 20px 0px rgba(1, 31, 69, 0.20), 0px 0px 1px 0px rgba(1, 31, 69, 0.31)",
    color: "#000"
});
